import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import {
    CoreAlert,
    CoreTypography,
    LoadingButton,
    useTheme
} from '@thriveglobal/thrive-web-leafkit'
import { CircularProgress } from '@mui/material'

interface LandingPageMainCtaProps {
    showPasswordField: boolean
    showSsoButton: boolean
    showMicrosoftButton: boolean
    loading: boolean
}

const buttonMessages = {
    continue: {
        type: 'continue',
        message: (
            <FormattedMessage
                description="Label for button prompting the user to continue to the next step"
                defaultMessage="Continue"
            />
        )
    },
    password: {
        type: 'password',
        message: (
            <FormattedMessage
                description="Label for button prompting the user to login"
                defaultMessage="Sign in"
            />
        )
    },
    sso: {
        type: 'sso',
        message: (
            <FormattedMessage
                description="Label for button prompting the user to login with SSO"
                defaultMessage="Sign in with SSO"
            />
        )
    }
}

type GetButtonMessageParamsType = Omit<LandingPageMainCtaProps, 'loading'>

function getButtonMessage({
    showPasswordField,
    showSsoButton,
    showMicrosoftButton
}: GetButtonMessageParamsType) {
    if (!showPasswordField && !showSsoButton && !showMicrosoftButton) {
        return buttonMessages.continue
    } else if (showPasswordField) {
        return buttonMessages.password
    } else if (showSsoButton) {
        return buttonMessages.sso
    }
}

const LandingPageMainCta: React.FC<LandingPageMainCtaProps> = ({
    loading,
    showPasswordField,
    showSsoButton,
    showMicrosoftButton
}) => {
    const buttonMessage = useMemo(
        () =>
            getButtonMessage({
                showPasswordField,
                showSsoButton,
                showMicrosoftButton
            }),
        [showPasswordField, showSsoButton, showMicrosoftButton]
    )
    const theme = useTheme()
    return (
        <>
            <LoadingButton
                loading={loading}
                loader={
                    <CircularProgress
                        data-testid="idpLoader"
                        size={24}
                        sx={{ verticalAlign: 'middle' }}
                    />
                }
                fixWidth
                size="large"
                variant="contained"
                color="info"
                type="submit"
                form="login-form"
                sx={{
                    width: '36rem',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginBottom: 2,
                    [theme.breakpoints.down('sm')]: {
                        width: '100%'
                    }
                }}
            >
                <CoreTypography customVariant="buttonLarge">
                    {buttonMessage.message}
                </CoreTypography>
            </LoadingButton>
        </>
    )
}

export default LandingPageMainCta
